/** Core domain types for the chain-drop merge engine. */

/** A tier definition (loaded from tiers.json). `level` is the merge ladder index (1-based). */
export interface Tier {
  level: number;
  key: string;
  color: string;
  glow: boolean;
  anim: string;
  points: number;
}

/**
 * A board cell:
 *   0          = empty
 *   1..N       = tier level
 *  WILD_CELL   = wild — merges with any adjacent tier (spec §3.6)
 *  BOMB_CELL   = bomb — clears a 3×3 area when it lands (spec §3.6)
 *
 * Specials use negative numbers so `Math.max` over the board still gives the
 * highest tier and the existing scoring/charging math is unaffected.
 */
export type Cell = number;

export const WILD_CELL = -1;
export const BOMB_CELL = -2;

/**
 * Boss cells encode their remaining HP in the value: `-100 - hp`.
 * A 3-HP boss is -103; taking damage moves it toward -100; at -100 it breaks
 * (becomes 0). Encoding HP in the cell keeps board snapshots (frames, sync)
 * self-contained — no parallel HP array to drift out of sync.
 */
export const BOSS_BASE = -100;
export const bossCell = (hp: number): Cell => BOSS_BASE - Math.max(1, Math.floor(hp));
export const isBoss = (cell: Cell): boolean => cell <= BOSS_BASE;
export const bossHp = (cell: Cell): number => (isBoss(cell) ? BOSS_BASE - cell : 0);

/** True for any special particle (wild, bomb, or boss). */
export const isSpecial = (cell: Cell): boolean => cell < 0;
export const isWild = (cell: Cell): boolean => cell === WILD_CELL;
export const isBomb = (cell: Cell): boolean => cell === BOMB_CELL;

/** Grid coordinate. `r` = row (0 = top), `c` = column (0 = left). */
export interface Pos {
  r: number;
  c: number;
}

/** Spawn weight table keyed by tier level (as string). e.g. { "1": 0.7, "2": 0.3 }. */
export type SpawnTable = Record<string, number>;

export interface BoardConfig {
  cols: number;
  rows: number;
  /** Cells that can never hold a piece (obstacles). */
  blockedCells?: ReadonlyArray<[number, number]>;
  /** Boss cells fixed on the board at start: [row, col, hp]. */
  bosses?: ReadonlyArray<[number, number, number]>;
  /** Cells pre-filled with tiers at start (puzzle mode): [row, col, tier]. */
  presetCells?: ReadonlyArray<[number, number, number]>;
  spawnTable: SpawnTable;
  /** Highest tier level that exists; merges at the cap do not promote further. */
  maxTier: number;
  /**
   * Minimum size of a same-tier group required to merge/promote. On a grid you
   * control placement exactly, so 2 is trivial — default is 3 for real
   * difficulty. Tunable per stage.
   */
  mergeMin?: number;
}

/** A single merge that occurred during resolution (for scoring + FX). */
export interface MergeEvent {
  /** Tier level the group was BEFORE promotion. */
  fromTier: number;
  /** Tier level AFTER promotion (fromTier + 1, capped at maxTier). */
  toTier: number;
  /** Number of cells that fused. */
  groupSize: number;
  /** Cell where the promoted piece landed. */
  result: Pos;
  /** Chain depth this merge belongs to (1 = first/trigger step). */
  chainLevel: number;
}

/** One animation frame in a resolution (snapshot of the board after a sub-step). */
export interface ResolveFrame {
  /** Board snapshot (copy of grid cells) after this step. */
  cells: Cell[];
  /** What produced this frame. */
  kind: "land" | "merge" | "gravity" | "bomb";
  /** Chain depth this frame belongs to (0 = initial landing). */
  chainLevel: number;
  /** Merges that happened in this frame (only for `kind === "merge"`). */
  merges: MergeEvent[];
  /** Cells cleared by a bomb in this frame (only for `kind === "bomb"`). */
  bombCleared?: Pos[];
  /** Position of the bomb that detonated (only for `kind === "bomb"`). */
  bombCenter?: Pos;
}

/** Result of resolving the board after a drop or ability. */
export interface ResolveResult {
  merges: MergeEvent[];
  /** Deepest chain level reached (0 if no merges). */
  maxChain: number;
  /** Raw points earned from this resolution (before meta multipliers). */
  score: number;
  /** Highest tier produced during this resolution. */
  highestTier: number;
  /** Combo-gauge charge earned from this resolution. */
  chargeGain: number;
  /** Boss cells destroyed during this resolution. */
  bossesBroken: number;
  /** Ordered animation frames (first = landing state, last = final stable state). */
  frames: ResolveFrame[];
}

/** Active ability identifiers (spec §3.6). */
export type AbilityId = "hammer" | "swap" | "promote" | "reroll";

export interface AbilityDef {
  id: AbilityId;
  /** Combo-gauge charge consumed per use. */
  cost: number;
  /** Cells the player must select to use it (0 = instant). */
  targets: number;
}

// ---- Campaign / stages (spec §5) ----

export type StageRule = "endless" | "drop_limit" | "time_limit";

/** Objective types (spec §5.4). All are "reach value" thresholds. */
export type ObjectiveType =
  | "reach_tier"
  | "score"
  | "merge_count"
  | "create_count"
  | "max_chain"
  | "clear_blocks"
  | "survive_drops"
  | "break_boss";

export interface Objective {
  type: ObjectiveType;
  /** Threshold value to reach. */
  value: number;
  /** For `create_count`: which tier level must be produced. */
  tier?: number;
}

/** Star condition types (spec §5.3 `stars`). */
export type StarType = "clear" | "drops_under" | "max_chain" | "score_over" | "no_ability";

export interface StarCond {
  type: StarType;
  value?: number;
}

export interface StageRewards {
  firstClear?: { stardust?: number; xp?: number; unlock?: string };
  perStar?: { stardust?: number; xp?: number };
  repeat?: { stardust?: number; xp?: number };
}

export interface Stage {
  id: string;
  world: number;
  chapter: number;
  order: number;
  gridCols: number;
  gridRows: number;
  rule: StageRule;
  objectives: Objective[];
  constraints: {
    dropLimit?: number | null;
    timeLimit?: number | null;
    blockedCells?: Array<[number, number]>;
    /** Boss cells at start: [row, col, hp] — break them by merging adjacent. */
    bosses?: Array<[number, number, number]>;
    /** Pre-filled tiers at start (puzzle mode): [row, col, tier]. */
    presetCells?: Array<[number, number, number]>;
    spawnTable: SpawnTable;
    allowedAbilities?: string[];
    /** Override the global merge threshold for this stage (default 3). */
    mergeMin?: number;
  };
  /** Star conditions keyed by star number "1".."3". */
  stars: Record<string, StarCond>;
  rewards: StageRewards;
  unlock?: { requiresStage?: string; minAccountLevel?: number };
}

/** Mutable per-run statistics, evaluated against objectives/stars. */
export interface RunStats {
  score: number;
  drops: number;
  merges: number;
  maxChain: number;
  maxTier: number;
  /** Count of promotions that produced each tier level. */
  tierCreated: Record<number, number>;
  blocksCleared: number;
  /** Boss cells destroyed this run (break_boss objective). */
  bossesBroken: number;
  abilitiesUsed: number;
  elapsedMs: number;
}
